/**
 * As informações da ficha em três colunas (Informação | Valor e situação | Fonte e detalhe), agrupadas como
 * no catálogo: contrato, pessoas e papéis, atos e registros (E139.F3, ADR-0095 §2.4 e §2.7).
 * E155.F3: o cartão "Rever leitura na fonte" abre numa linha a mais, logo abaixo do campo.
 */
import { Fragment } from 'react';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import {
  camposPorGrupo,
  type CampoDaFichaData,
  type FichaDaLeituraData,
  type PaginaNaFonte,
} from '@/lib/ficha_da_leitura';
import { chaveDaReleitura } from '@/lib/releitura_da_leitura';

import { LinhaDoCampo } from './LinhaDoCampo';
import { t } from './textos';

export interface CamposDaFichaProps {
  ficha: FichaDaLeituraData;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
  /** A chave do campo com o cartão aberto (`chaveDaReleitura`). */
  aberto?: string | null;
  onReverLeitura?: ((campo: CampoDaFichaData) => void) | undefined;
  /** O cartão, desenhado abaixo da linha aberta. */
  cartao?: JSX.Element | null;
}

export function CamposDaFicha({
  ficha,
  onVerNaFonte,
  aberto = null,
  onReverLeitura,
  cartao = null,
}: CamposDaFichaProps): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <Table data-testid="ficha-campos">
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/4">{t.colunas.informacao}</TableHead>
            <TableHead className="w-2/5">{t.colunas.valor}</TableHead>
            <TableHead>{t.colunas.fonte}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {camposPorGrupo(ficha).map(({ grupo, campos }) => (
            <Fragment key={grupo}>
              <TableRow data-testid="ficha-grupo">
                <TableCell colSpan={3} className="pt-sc-4 font-semibold text-fg">
                  {t.grupo[grupo]}
                </TableCell>
              </TableRow>
              {campos.map((campo) => {
                const chave = chaveDaReleitura(campo.campo, campo.referente_id);
                const estaAberto = aberto === chave && cartao !== null;
                return (
                  <Fragment key={chave}>
                    <LinhaDoCampo
                      campo={campo}
                      onVerNaFonte={onVerNaFonte}
                      aberto={estaAberto}
                      onReverLeitura={onReverLeitura}
                    />
                    {estaAberto ? (
                      <TableRow data-testid="ficha-releitura-linha">
                        <TableCell colSpan={3} className="p-sc-2">
                          {cartao}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
